import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { styled } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import { useLazyGetCompanyIndividualsQuery } from "marketplace/companies/_api/company-api";
import React from "react";
import {
  type TeamPeopleResults,
  type TeamPickerEntry,
  useCompanyPeople,
  withoutPeopleOf,
} from "work/work-agreements/teams/people-picker/_hooks/use-team-people-results";
import {
  personFromIndividual,
  type TeamPickerPerson,
  worksFor,
} from "work/work-agreements/teams/people-picker/team-picker-person";
import type TeamPeopleSelection from "work/work-agreements/teams/people-picker/team-people-selection";

/**
 * What a row may say about one person: whether they can be chosen here and, when not, why. The
 * picker never leaves somebody out silently — a person already on the team, or one the team would
 * refuse, is drawn with the reason.
 */
export type TeamPickerPersonStatus = { choosable: true } | { choosable: false; checked: boolean; reason: string };

export type SelectionUpdate = (update: (selection: TeamPeopleSelection) => TeamPeopleSelection) => void;

type TeamPeopleResultsListProps = {
  results: TeamPeopleResults;
  selection: TeamPeopleSelection;
  statusOf: (person: TeamPickerPerson) => TeamPickerPersonStatus;
  /** Whether a group row can be checked as a whole. Not in leader mode, which picks one person. */
  groupsAreCheckable: boolean;
  /**
   * A legal entity whose people are left out altogether, not drawn with a reason: the client's own
   * firm, when the client staffs the vendor team (#6859). Nobody from it could ever be added here.
   */
  excludedLegalEntityId?: string;
  emptyMessage: string;
  onSelectionChange: SelectionUpdate;
};

const GroupRow = styled(ListItem)(({ theme }) => ({
  backgroundColor: theme.palette.action.hover,
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const PersonRow = styled(ListItem, { shouldForwardProp: (prop) => prop !== "nested" })<{ nested?: boolean }>(
  ({ theme, nested }) => ({
    borderBottom: `1px solid ${theme.palette.divider}`,
    paddingLeft: nested ? theme.spacing(9) : theme.spacing(2),
  }),
);

const Reason = styled(Typography)(({ theme }) => ({
  ...theme.typography.caption,
  color: theme.palette.text.secondary,
  flexShrink: 0,
  marginLeft: theme.spacing(1),
  maxWidth: "45%",
  textAlign: "right",
}));

const Message = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  padding: theme.spacing(2),
}));

function Expander(props: Readonly<{ expanded: boolean; name: string; onToggle: () => void }>) {
  return (
    <IconButton
      edge="start"
      size="small"
      aria-label={`${props.expanded ? "Collapse" : "Expand"} ${props.name}`}
      aria-expanded={props.expanded}
      onClick={props.onToggle}
    >
      <ChevronRightIcon sx={{ transform: props.expanded ? "rotate(90deg)" : "none", transition: "transform 120ms" }} />
    </IconButton>
  );
}

function PersonListRow(
  props: Readonly<{ person: TeamPickerPerson; nested?: boolean } & Pick<TeamPeopleResultsListProps, "selection" | "statusOf" | "onSelectionChange">>,
) {
  const { person, selection, statusOf, onSelectionChange } = props;
  const status = statusOf(person);
  const checked = status.choosable ? selection.has(person.userId) : status.checked;
  const labelId = `team-picker-person-${person.userId}`;
  return (
    <PersonRow nested={props.nested} dense>
      <ListItemIcon sx={{ minWidth: 40 }}>
        <Checkbox
          edge="start"
          checked={checked}
          disabled={!status.choosable}
          slotProps={{ input: { "aria-labelledby": labelId } }}
          onChange={() => onSelectionChange((current) => current.toggle(person))}
        />
      </ListItemIcon>
      {/* The checkbox is named by the person alone. Labelled by the whole item, it was "Sara Lind
          Meridian IP Services", and a screen reader read the firm as part of the name. */}
      <ListItemText primary={person.name} secondary={person.detail} slotProps={{ primary: { id: labelId } }} />
      {!status.choosable && <Reason>{status.reason}</Reason>}
    </PersonRow>
  );
}

type GroupProps = Pick<
  TeamPeopleResultsListProps,
  "selection" | "statusOf" | "groupsAreCheckable" | "excludedLegalEntityId" | "onSelectionChange"
>;

function groupSummary(kind: string, people: number | undefined, chosen: number): string {
  const size = people === undefined ? kind : `${kind} · ${people} ${people === 1 ? "person" : "people"}`;
  return chosen ? `${size} · ${chosen} selected` : size;
}

function GroupCheckbox(props: Readonly<{ name: string; state: "none" | "some" | "all"; disabled: boolean; onToggle: () => void }>) {
  return (
    <Checkbox
      checked={props.state === "all"}
      indeterminate={props.state === "some"}
      disabled={props.disabled}
      slotProps={{ input: { "aria-label": `Select everyone in ${props.name}` } }}
      onChange={props.onToggle}
    />
  );
}

/** A marketplace team: its people arrived with it, so checking it chooses them at once. */
function TeamGroup(props: Readonly<{ name: string; people: TeamPickerPerson[] } & GroupProps>) {
  const { name, people, selection, statusOf, groupsAreCheckable, onSelectionChange } = props;
  const [expanded, setExpanded] = React.useState(false);
  const canChoose = (person: TeamPickerPerson) => statusOf(person).choosable;
  const state = selection.groupState(people, canChoose);
  const chosen = people.filter((person) => selection.has(person.userId)).length;
  return (
    <>
      <GroupRow dense>
        <Expander expanded={expanded} name={name} onToggle={() => setExpanded(!expanded)} />
        {groupsAreCheckable && (
          <GroupCheckbox
            name={name}
            state={state}
            disabled={!people.some(canChoose)}
            onToggle={() => onSelectionChange((current) => current.toggleGroup(people, canChoose))}
          />
        )}
        <ListItemText primary={name} secondary={groupSummary("Team", people.length, chosen)} />
      </GroupRow>
      {expanded &&
        people.map((person) => (
          <PersonListRow
            key={person.userId}
            nested
            person={person}
            selection={selection}
            statusOf={statusOf}
            onSelectionChange={onSelectionChange}
          />
        ))}
    </>
  );
}

/**
 * A company: its people are fetched when it is opened. Checking it before then fetches them first, so
 * the checkbox chooses the company's people rather than nobody.
 */
function CompanyGroup(props: Readonly<{ companyId: string; name: string } & GroupProps>) {
  const { companyId, name, selection, statusOf, groupsAreCheckable, excludedLegalEntityId, onSelectionChange } = props;
  const [expanded, setExpanded] = React.useState(false);
  // A check that fetched the people and failed. Shown whether or not the company is open, because
  // the checkbox is where the viewer pressed and it chose nobody.
  const [checkFailed, setCheckFailed] = React.useState(false);
  const [loadPeople, lazyResult] = useLazyGetCompanyIndividualsQuery();
  const company = useCompanyPeople(companyId, expanded);
  const offered = (person: TeamPickerPerson) => !worksFor(person, excludedLegalEntityId);
  const people = company.people.filter(offered);
  const canChoose = (person: TeamPickerPerson) => statusOf(person).choosable;
  const state = selection.groupState(people, canChoose);
  const chosen = people.filter((person) => selection.has(person.userId)).length;
  const isLoading = company.isLoading || lazyResult.isFetching;

  async function toggleCompany() {
    if (company.isLoaded) {
      onSelectionChange((current) => current.toggleGroup(people, canChoose));
      return;
    }
    setCheckFailed(false);
    const fetched = await loadPeople({ companyId }, true).unwrap().catch(() => undefined);
    if (!fetched) {
      setCheckFailed(true);
      return;
    }
    const fetchedPeople = fetched.map(personFromIndividual).filter(offered);
    onSelectionChange((current) => current.toggleGroup(fetchedPeople, canChoose));
    setExpanded(true);
  }

  return (
    <>
      <GroupRow dense>
        <Expander expanded={expanded} name={name} onToggle={() => setExpanded(!expanded)} />
        {groupsAreCheckable && (
          <GroupCheckbox
            name={name}
            state={state}
            disabled={company.isLoaded && !people.some(canChoose)}
            onToggle={() => void toggleCompany()}
          />
        )}
        <ListItemText primary={name} secondary={groupSummary("Company", company.isLoaded ? people.length : undefined, chosen)} />
        {isLoading && <CircularProgress size={18} aria-label={`Loading the people at ${name}`} />}
      </GroupRow>
      {checkFailed && !lazyResult.isFetching && (
        <Message role="alert">
          The people at {name} could not be loaded, so nobody was selected.{" "}
          <Button size="small" onClick={() => void toggleCompany()}>
            Try again
          </Button>
        </Message>
      )}
      {expanded && company.isError && <Message>The people at {name} could not be loaded.</Message>}
      {expanded && company.isLoaded && people.length === 0 && <Message>Nobody at {name} can be shown to you.</Message>}
      {expanded &&
        people.map((person) => (
          <PersonListRow
            key={person.userId}
            nested
            person={person}
            selection={selection}
            statusOf={statusOf}
            onSelectionChange={onSelectionChange}
          />
        ))}
    </>
  );
}

function EntryRow(props: Readonly<{ entry: TeamPickerEntry } & GroupProps>) {
  const { entry, ...rest } = props;
  switch (entry.kind) {
    case "person":
      return <PersonListRow person={entry.person} selection={rest.selection} statusOf={rest.statusOf} onSelectionChange={rest.onSelectionChange} />;
    case "team":
      return <TeamGroup name={entry.name} people={entry.people} {...rest} />;
    case "company":
      return <CompanyGroup companyId={entry.companyId} name={entry.name} {...rest} />;
    case "unavailable":
      return (
        <PersonRow dense>
          <ListItemText primary={entry.name} secondary={entry.reason} />
        </PersonRow>
      );
  }
}

/** What a source with more matches than it shows says, so nobody takes the first page for all of them. */
export const TRUNCATED_RESULTS_MESSAGE = "Showing the first matches only. Type in the filter to narrow them.";
/**
 * What a source says when it holds more matches than it shows and none of the first page can be
 * offered: the client's own people, left out, can fill it (#6859). Saying nobody matches would hide
 * the people past it.
 */
export const TRUNCATED_NONE_OFFERED_MESSAGE = "None of the first matches can be added here. Type in the filter to narrow them.";

/** One source's results: people, and groups of people that expand and check as one. */
export default function TeamPeopleResultsList(props: Readonly<TeamPeopleResultsListProps>) {
  const { results, emptyMessage, ...rest } = props;
  const entries = withoutPeopleOf(results.entries, props.excludedLegalEntityId);
  if (results.isError) return <Message role="alert">These results could not be loaded.</Message>;
  if (entries.length === 0) {
    if (results.isLoading) return <CircularProgress sx={{ m: 2 }} aria-label="Loading results" />;
    return <Message>{results.isTruncated ? TRUNCATED_NONE_OFFERED_MESSAGE : emptyMessage}</Message>;
  }
  return (
    <>
      <List disablePadding aria-label="Results">
        {entries.map((entry) => (
          <EntryRow key={entry.key} entry={entry} {...rest} />
        ))}
      </List>
      {results.isTruncated && <Message>{TRUNCATED_RESULTS_MESSAGE}</Message>}
    </>
  );
}
